import { Component, createRef, isValidElement } from 'react';
import cn from 'classnames';
import IconClose from '@creditflowresearch/uilib/Icon/IconClose';
import PortalWrp from '@creditflowresearch/uilib/Layout/PortalWrp';
import headerStyles from '@creditflowresearch/uilib/styles/headers.module.scss';
import hHidden from '@creditflowresearch/uilib/styles/helpers/hidden.module.scss';
import hInteraction from '@creditflowresearch/uilib/styles/helpers/interaction.module.scss';
import hText from '@creditflowresearch/uilib/styles/helpers/text.module.scss';
import styles from './modal.module.scss';
import IconButton from '@creditflowresearch/uilib/IconButton';

export interface IProps {
  /**
   * Content to be displayed inside the modal.
   */
  children?: React.ReactNode,
  /**
   * Content to be displayed in the footer of the modal.
   */
  footer?: React.ReactNode,
  /**
   * CSS class name for the modal.
   */
  className?: string,
  /**
   * Determines if the modal should be rendered inside a portal.
   */
  usePortal?: boolean,
  /**
   * Flag to control the visibility of the modal.
   */
  isVisible?: boolean,
  /**
   * Callback function triggered when the close button is clicked.
   */
  onCloseClicked?: (e?: any) => void,
  /**
   * Determines if an overlay should be displayed behind the modal.
   */
  showOverlay?: boolean,
  /**
   * Callback function triggered when the overlay is clicked.
   */
  onOverlayClicked?: () => void,
  /**
   * Title of the modal.
   */
  title?: React.ReactNode | string,
  /**
   * Determines if the modal should close when the escape key is pressed.
   */
  closeOnEsc?: boolean,
  /**
   * Determines if a close button should be displayed.
   */
  showCloseButton?: boolean,
  /**
   * Callback function triggered after the modal closes.
   */
  afterClose?: () => void,
  /**
   * Callback function triggered after the modal opens.
   */
  afterOpen?: () => void,
  /**
   * Determines if the modal should close when the overlay is clicked.
   */
  hideOnOverlayClicked?: boolean,
  /**
   * CSS class name for the modal title.
   */
  modalTitleClassName?: string,
  /**
   * CSS class name for the modal body.
   */
  modalBodyClassName?: string,
  /**
   * CSS class name for the modal dialog.
   */
  modalDialogClassName?: string,
  /**
   * Unique identifier for testing purposes.
   */
  dataTest: string,
  /**
   * Callback function for handling close click.
   */
  handleCloseClick?: () => void,
  /**
   * Callback function triggered on context menu event within the modal.
   */
  onContextMenu?: (e?: React.MouseEvent<HTMLDivElement>) => void,
  /**
   * Ref callback for the footer element.
   */
  footerRefCallback?: React.RefCallback<any>,
  /**
   * Flag to use custom presentation for the modal.
   */
  isCustomPresentation?: boolean,
  /**
   * URL for a custom logo to be displayed in the modal.
   */
  customLogoUrl?: string,
}

interface IState {
  isVisible: boolean,
  modalContentElement: HTMLElement | null,
  clientHeight: number,
}

const defaultProps: Partial<IProps> = {
  title: '',
  showOverlay: true,
  closeOnEsc: true,
  className: '',
  modalTitleClassName: '',
  modalBodyClassName: '',
  modalDialogClassName: '',
  hideOnOverlayClicked: false,
  isVisible: false,
  showCloseButton: true,
  isCustomPresentation: false,
};

export const isEscapeKey = (event: KeyboardEvent): boolean => {
  let isEscape = false;

  if (event.key !== undefined) {
    isEscape = (event.key === 'Escape' || event.key === 'Esc');
  } else {
    isEscape = (event.keyCode === 27);
  }
  return isEscape;
};

const isOpening = (prevState, nextState) => !prevState.isVisible && nextState.isVisible;
const isClosing = (prevState, nextState) => prevState.isVisible && !nextState.isVisible;
class Modal extends Component<IProps, IState> {
  static defaultProps: Partial<IProps> = defaultProps;

  // eslint-disable-next-line react/sort-comp
  protected modal: React.RefObject<HTMLDivElement>;

  protected modalContent: React.RefObject<HTMLDivElement>;

  constructor(props: IProps) {
    super(props);
    this.state = {
      isVisible: props.isVisible,
      modalContentElement: null,
      clientHeight: 0,
    };
    this.modal = createRef();
    this.modalContent = createRef();
    this._handlerEsc = this._handlerEsc.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleCloseClick = this.handleCloseClick.bind(this);
    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);
    this.handleModalClick = this.handleModalClick.bind(this);
    this.enableEscEventListener = this.enableEscEventListener.bind(this);
    this.disableEscEventListener = this.disableEscEventListener.bind(this);
    this.checkoutRef = this.checkoutRef.bind(this);
  }

  componentDidMount() {
    if (this.state.isVisible) {
      this.enableEscEventListener();
      document.body.classList.add(hInteraction.nonScrollable);

      if (this.props.afterOpen) {
        this.props.afterOpen();
      }
    }
  }

  // @ts-ignore
  componentDidUpdate(prevProps, prevState) {
    if (this.props.isVisible !== this.state.isVisible) {
      this.setState({
        isVisible: !!this.props.isVisible,
      });
    }

    if (isOpening(prevState, this.state)) {
      document.body.classList.add(hInteraction.nonScrollable);

      this.enableEscEventListener();

      if (this.props.afterOpen) {
        this.props.afterOpen();
      }
    }

    if (isClosing(prevState, this.state)) {
      document.body.classList.remove(hInteraction.nonScrollable);

      this.disableEscEventListener();

      if (this.props.afterClose) {
        this.props.afterClose();
      }
    }
    if (this.state.clientHeight !== document.body.clientHeight) {
      this.setState({ clientHeight: document.body.clientHeight });
    }
  }

  componentWillUnmount() {
    this.disableEscEventListener();
    document.body.classList.remove(hInteraction.nonScrollable);
  }

  checkoutRef(ref) {
    if (ref) this.setState({ modalContentElement: ref });
    else this.setState({ modalContentElement: null });
  }

  /**
   * @param {SyntheticEvent} event
   * @private
   */
  _handlerEsc(event) {
    if (isEscapeKey(event) && this.props.closeOnEsc && this.state.isVisible) {
      this.handleCloseClick();
      if (document.activeElement !== document.body) {
        // @ts-ignore
        document.activeElement.blur();
      }
    }
  }

  enableEscEventListener() {
    document.addEventListener('keydown', this._handlerEsc);
  }

  disableEscEventListener() {
    document.removeEventListener('keydown', this._handlerEsc);
  }

  handleModalClick(event) {
    event.stopPropagation();
  }

  handleOverlayClick() {
    if (this.props.hideOnOverlayClicked) {
      this.handleCloseClick();
    }
    if (this.props.onOverlayClicked) {
      this.props.onOverlayClicked();
    }
  }

  handleCloseClick() {
    if (this.props.handleCloseClick) {
      this.props.handleCloseClick();
      return false;
    }
    this.hide();
    if (this.props.onCloseClicked) {
      this.props.onCloseClicked();
    }
    return true;
  }

  show() {
    this.setState({ isVisible: true });
  }

  hide() {
    this.setState({ isVisible: false });
  }

  /**
   * @return {ReactElement}
   */
  render() {
    let title;
    if (isValidElement(this.props.title)) {
      title = this.props.title;
    } else {
      title = this.props.title
        ? <span className={ cn(headerStyles.isH2, 'modalHeaderTitle') }>{ this.props.title }</span>
        : null;
    }

    let modal = (
      <div
        ref={ this.modal }
        className={ cn('modalDialog', this.props.modalDialogClassName) }
        onClick={ (event) => this.handleModalClick(event) }
        data-test={ this.props.dataTest }
      >
        <div className={ cn('modalContent', { modalContentWithFooter: !!this.props.footer }) }>
          <div className="modalHeaderWrp">
            { !!title && (
              <div
                className={ cn('modalHeader', this.props.modalTitleClassName) }
                data-test="modalHeader"
              >
                { title }
                { this.props.customLogoUrl && !this.props.isCustomPresentation && (
                  <img
                    src={ this.props.customLogoUrl }
                    alt="logo"
                    className={ headerStyles.disclaimerLogo }
                  />
                ) }
              </div>
            ) }
            { this.props.showCloseButton && (
              <IconButton
                icon={ <IconClose /> }
                className="modalCloseBtn"
                onClick={ this.handleCloseClick }
                dataTest="closeModal"
                ariaLabel="Close"
              />
            ) }
          </div>
          <div className={ cn('modalBody', this.props.modalBodyClassName, hText.base) }>
            { this.props.children }
          </div>
        </div>
        { this.props.footer && (
          <div
            className="modalFooter"
            ref={ this.props.footerRefCallback }
          >
            { this.props.footer }
          </div>
        ) }
      </div>
    );

    const renderModalContent = () => {
      const shouldRenderAllHeight = this.state.clientHeight !== 0 &&
        this.state.modalContentElement?.clientHeight > this.state.clientHeight;

      if (this.props.isCustomPresentation) {
        return (
          <div
            className={ cn('customModalWrapper', {
              customModalWrapperDisclaimer: this.props.title === 'Disclaimer',
              customModalFullHeight: shouldRenderAllHeight,
            }) }
            ref={ this.checkoutRef }
          >
            { this.props.customLogoUrl && this.props.isCustomPresentation && (
              <div className={ headerStyles.logoWrapper }>
                <img
                  src={ this.props.customLogoUrl }
                  alt="logo"
                  className={ cn(headerStyles.disclaimerLogoOnTop, 'disclaimerLogoOnTop') }
                />
              </div>
            ) }
            { modal }
          </div>
        );
      }
      return modal;
    };

    let content = renderModalContent();
    const isVisibleCls = { [hHidden.hidden]: !this.state.isVisible };
    let wrapperClasses = cn(styles.modalWrp, isVisibleCls, this.props.className);

    if (this.props.showOverlay) {
      wrapperClasses = cn(wrapperClasses, styles.withOverlay);
      content = (
        <div
          className={ cn('modalOverlay', isVisibleCls) }
          onClick={ this.handleOverlayClick }
          data-no-dnd="true"
        >
          { modal }
        </div>
      );
    }

    let render = (
      <div
        className={ wrapperClasses }
        onContextMenu={ this.props.onContextMenu }
      >
        { content }
      </div>
    );

    return (
      <PortalWrp usePortal={ this.props.usePortal }>{ render }</PortalWrp>
    );
  }
}

export default Modal;
